import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { DueStageCards, STAGES } from "@/components/dues/DueStageCards";
import { DueCustomerRow } from "@/components/dues/DueCustomerRow";

export const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function DuesPage() {
  const [data, setData] = useState(null);
  const [stage, setStage] = useState("all");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/dues/aging");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Due list load nahi hui");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return (data?.customers || []).filter((c) =>
      (stage === "all" || c.stage === stage) &&
      (!ql || (c.customer_name || "").toLowerCase().includes(ql) || c.mobile.includes(ql)));
  }, [data, stage, q]);

  const overdue = data?.summary?.m1;

  return (
    <div className="space-y-5" data-testid="dues-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-heading font-black text-slate-100">Due Recovery</h1>
          <p className="text-sm text-slate-400">Sabse purane unpaid bill ki date se din gine gaye hain</p>
        </div>
        <div className="text-right">
          <div className="text-[10px] tracking-widest text-slate-500">TOTAL DUE</div>
          <div className="text-2xl font-mono-num font-bold text-red-400" data-testid="dues-total">{inr(data?.total_due)}</div>
        </div>
      </div>

      {overdue?.count > 0 && (
        <div className="flex items-center gap-2 rounded-sm border border-red-500/50 bg-red-500/10 px-4 py-3 text-red-300 text-sm" data-testid="dues-overdue-alert">
          <WarningCircle size={18} weight="fill" />
          <span><b>{overdue.count}</b> customers ka {inr(overdue.amount)} 1 month se zyada purana due hai</span>
        </div>
      )}

      <DueStageCards summary={data?.summary} total={data?.customers?.length || 0} active={stage} onPick={setStage} />

      <div className="relative max-w-sm">
        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={14} />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Naam ya mobile se search…"
          className="pl-8 bg-slate-800/60 border-slate-600 text-white h-9 rounded-sm text-sm" data-testid="dues-search" />
      </div>

      <div className="space-y-2" data-testid="dues-list">
        {!data ? (
          <div className="p-6 text-center text-slate-500 text-sm">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-slate-500 text-sm" data-testid="dues-empty">
            {stage === "all" ? "Koi due nahi hai." : `${STAGES.find((s) => s.key === stage)?.label} mein koi due nahi.`}
          </div>
        ) : rows.map((c) => (
          <DueCustomerRow key={c.mobile} c={c} business={data.business} onSent={load} />
        ))}
      </div>
    </div>
  );
}
